import React, { useCallback, useState } from "react";
import * as Popover from "@radix-ui/react-popover";
import { usePersistStore } from "../store";
import { NOTIFICATION_STATUS } from "../utils/enums";
import { useNavigate } from "react-router-dom";
import { Utils } from "../utils";
import {
  useNotifications,
  useMarkNotificationRead,
  useMarkAllNotificationsRead,
} from "../hooks/queries";

const Notification = ({ n, onClose }: any) => {
  const navigate = useNavigate();
  const { user } = usePersistStore((s: any) => s);
  const markRead = useMarkNotificationRead();
  const isUnread = n?.status === NOTIFICATION_STATUS.UNREAD;

  const handleClick = useCallback(
    (e: any) => {
      e.preventDefault();
      // Fire-and-forget: the mutation marks it read and invalidates the list.
      if (isUnread) markRead.mutate(n?._id);
      onClose?.();
      const dest = n?.link || (n?.adId ? `/details/${n?.adId}` : null);
      if (dest) navigate(dest);
    },
    [isUnread, n?._id, n?.adId, n?.link, onClose, navigate, markRead]
  );

  return (
    <div
      className={`notifCard ${isUnread ? "notifCard--unread" : ""}`}
      onClick={handleClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") handleClick(e);
      }}
    >
      <span
        className={`notifCard__dot ${
          isUnread ? "" : "notifCard__dot--placeholder"
        }`}
        aria-hidden="true"
      />
      <div className="notifCard__body">
        <h4 className="notifCard__title">{n?.title}</h4>
        <p className="notifCard__description">{n?.description}</p>
        <div className="notifCard__meta">
          {Utils.formatDateTimeInTz(n?.createdAt, user?.timezone)}
        </div>
      </div>
    </div>
  );
};

const NotificationsPopup: React.FC = () => {
  const { token } = usePersistStore((s: any) => s);
  const [isOpen, setIsOpen] = useState<boolean>(false);

  // Cached + polled (30s, paused while the tab is hidden) by useNotifications.
  const { data, refetch } = useNotifications(token);
  const list: any[] = data || [];
  const unreadCount = list.filter(
    (k: any) => k?.status === NOTIFICATION_STATUS.UNREAD
  ).length;

  const markAll = useMarkAllNotificationsRead();

  const handleOpenChange = useCallback(
    (next: boolean) => {
      setIsOpen(next);
      if (next) refetch();
    },
    [refetch]
  );

  // Wired to the "Mark all as read" button. Notifications are otherwise
  // marked read individually as the user clicks them.
  const markAllAsRead = useCallback(() => {
    if (unreadCount === 0) return;
    markAll.mutate();
  }, [markAll, unreadCount]);

  return (
    <Popover.Root open={isOpen} onOpenChange={handleOpenChange}>
      <Popover.Trigger asChild>
        <div className="d-flex notifBell">
          <i
            className="bi bi-bell-fill notifBell__icon"
            aria-label="Notifications"
          />
          {unreadCount > 0 && (
            <div
              style={{
                background: "red",
                color: "white",
                width: 20,
                height: 20,
                textAlign: "center",
                borderRadius: "50%",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                fontSize: 13,
                marginLeft: -10,
                marginTop: -10,
              }}
            >
              {unreadCount}
            </div>
          )}
        </div>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          sideOffset={12}
          align="end"
          className="notifPopup"
          onOpenAutoFocus={(e) => e.preventDefault()}
        >
          <div className="notifPopup__header">
            <div className="notifPopup__title">
              <span>Notifications</span>
              {unreadCount > 0 && (
                <span className="notifPopup__countPill">{unreadCount}</span>
              )}
            </div>
            <button
              type="button"
              className="notifPopup__markAll"
              onClick={markAllAsRead}
              disabled={unreadCount === 0}
            >
              Mark all as read
            </button>
          </div>

          <div className="notifPopup__list">
            {list?.length === 0 ? (
              <div className="notifPopup__empty">
                <div className="notifPopup__emptyIcon">
                  <i className="bi bi-bell" aria-hidden="true" />
                </div>
                <p className="notifPopup__emptyText">
                  You're all caught up.
                </p>
              </div>
            ) : (
              list?.map((n: any, idx: number) => (
                <Notification
                  n={n}
                  key={n?._id || idx}
                  onClose={() => setIsOpen(false)}
                />
              ))
            )}
          </div>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
};

export default NotificationsPopup;
